import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, forkJoin, from, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { environment } from '../../../../../environments/environment';
import { MasterService } from '../../../master/services/master.service';
import { MasterModel } from '../../../master/models/master.model';
import { bankAccountOptions, EntityFormOption } from '../../../finance/entity-form/entity-form.utils';
import { PolizaCreditoService } from '../../../../services/finanzas/poliza-credito/poliza-credito.service';
import { unwrapV2, V2ApiResponse } from '../../../shared/utils/v2-api-response.util';
import { RealStateInvestmentFormLookups, RealStateInvestmentProviderLookup } from '../models/real-state-investment.model';
import { RealStateInvestmentTaxModel } from '../models/summary-real-state-investment.model';

export interface RealStateInvestmentFormLookupsResult extends RealStateInvestmentFormLookups {
  taxes: RealStateInvestmentTaxModel[];
}

export type RealStatePayLookupKind = 'bankAccounts' | 'paymentCards' | 'cashRegisters' | 'partners' | 'creditPolicies';

const CREDIT_CARD_TYPE = 1;

@Injectable({ providedIn: 'root' })
export class RealStateInvestmentFormLookupsFacade {

  private readonly payCache = new Map<RealStatePayLookupKind, Observable<EntityFormOption[]>>();

  constructor(
    private http: HttpClient,
    private masterService: MasterService,
    private polizaCreditoService: PolizaCreditoService,
  ) { }

  payOptions(kind: RealStatePayLookupKind): Promise<EntityFormOption[]> {
    let entry = this.payCache.get(kind);
    if (!entry) {
      entry = this.requestPayOptions(kind).pipe(
        catchError(error => {
          this.payCache.delete(kind);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.payCache.set(kind, entry);
    }
    return firstValueFrom(entry.pipe(map(list => list.slice())));
  }

  private requestPayOptions(kind: RealStatePayLookupKind): Observable<EntityFormOption[]> {
    switch (kind) {
      case 'bankAccounts':
        return this.masterService.getBankAccounts().pipe(map(list => bankAccountOptions(list)));
      case 'paymentCards':
        return this.masterService.getPaymentCards(CREDIT_CARD_TYPE).pipe(map(list => (list || []).map(card => ({ id: card.id, display: card.display }))));
      case 'cashRegisters':
        return this.masterService.getCashRegisters().pipe(map(list => (list || [])
          .filter(item => item.metadata && item.metadata.id)
          .map(item => ({ id: item.metadata.id, display: item.display }))));
      case 'partners':
        return this.masterService.getParnerts().pipe(map(list => (list || []).map(partner => ({ id: Number(partner.id), display: partner.display }))));
      default:
        return from(this.polizaCreditoService.listPayments()).pipe(
          map((list: any[]) => (list || []).map(policy => ({ id: policy.uid, display: [policy.banco, policy.iban].filter(Boolean).join(' · ') || 'Póliza de crédito' }))),
        );
    }
  }

  loadAll(): Promise<RealStateInvestmentFormLookupsResult> {
    return firstValueFrom(forkJoin({
      entity: this.formLookups(),
      taxes: this.masterService.getTaxes(),
    })).then(result => ({
      accounts: (result.entity && result.entity.accounts) || [],
      providers: (result.entity && result.entity.providers) || [],
      taxes: this.mapTaxes(result.taxes),
    }));
  }

  providers(): Promise<RealStateInvestmentProviderLookup[]> {
    return this.formLookups().then(result => (result && result.providers) || []);
  }

  private formLookups(): Promise<RealStateInvestmentFormLookups> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<RealStateInvestmentFormLookups>>(environment.apiUrl + '/api/v2/real-state-investments/form-lookups')),
      'No se pudieron cargar los datos del formulario'
    );
  }

  private mapTaxes(list: MasterModel[] | null | undefined): RealStateInvestmentTaxModel[] {
    return (list || [])
      .filter(tax => tax && tax.metadata && tax.metadata.id !== undefined && tax.metadata.id !== null)
      .map(tax => ({
        id: Number(tax.metadata.id),
        name: tax.metadata.name || tax.display,
        taxImport: Number(tax.metadata.import) || 0,
        type: tax.metadata.type || '',
      }));
  }
}
